// Public screen opened from the QR code on the employee ID card (/id-card-verify/:token). No login:
// the token in the link is the only input. The API returns only photo, employee code and active status.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { idCardVerifyDataApi } from "../../../text/apidata";

const ORANGE = "#eb5a2b";

export default function IdCardVerify() {
  const { token } = useParams();
  // no token in the link (opened again after the token was hidden) -> ask to scan again
  const [state, setState] = useState(token ? "loading" : "noToken"); // loading | found | invalid | error | noToken
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    fetch(idCardVerifyDataApi + encodeURIComponent(token) + "/")
      .then((res) => {
        if (!res.ok) throw new Error("bad response");
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        setData(json);
        setState(json.valid ? "found" : "invalid");
        // the API has answered: hide the token from the address bar (and the history entry), the page keeps
        // showing the result. The token stays only in memory, so nobody can copy or share it from the link.
        window.history.replaceState(null, "", "/id-card-verify");
      })
      .catch(() => !cancelled && setState("error"));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const band = { padding: "16px 12px", color: "#fff", fontWeight: 700, fontSize: "clamp(16px, 4.8vw, 20px)" };
  const photoSize = "clamp(110px, 34vw, 170px)";
  const rowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "14px 20px", borderTop: "1px solid #eee", fontSize: "clamp(14px, 4vw, 17px)" };
  const active = state === "found" && data.is_active;

  return (
    <div
      style={{
        minHeight: "100dvh",
        boxSizing: "border-box",
        background: "#f3f5f6",
        padding: "16px",
        fontFamily: "Arial, sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          background: "#fff",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.1)",
          textAlign: "center",
        }}
      >
        {state === "loading" && <div style={{ padding: 40, color: "#737373" }}>Verifying ID card...</div>}

        {state === "found" && (
          <>
            <div style={{ ...band, background: active ? "#1f9d6b" : "#c0392b" }}>
              {active ? "✔ Valid UDS ID Card" : "✖ Employee Not Active"}
            </div>
            {data.photo_url ? (
              <img
                src={data.photo_url}
                alt=""
                style={{ width: photoSize, height: photoSize, borderRadius: "50%", objectFit: "cover", margin: "24px auto 8px", display: "block", border: `3px solid ${ORANGE}` }}
              />
            ) : (
              <div style={{ width: photoSize, height: photoSize, borderRadius: "50%", background: "#e2e8eb", margin: "24px auto 8px", border: `3px solid ${ORANGE}`, boxSizing: "border-box" }} />
            )}
            <div style={{ marginTop: 16, textAlign: "left" }}>
              <div style={rowStyle}>
                <span style={{ color: "#737373" }}>Employee Code</span>
                <span style={{ fontWeight: 700, wordBreak: "break-word", textAlign: "right" }}>{data.employee_code}</span>
              </div>
              <div style={rowStyle}>
                <span style={{ color: "#737373" }}>Status</span>
                <span style={{ fontWeight: 700, color: active ? "#1f9d6b" : "#c0392b" }}>{active ? "Active" : "Not Active"}</span>
              </div>
            </div>
          </>
        )}

        {state === "invalid" && (
          <>
            <div style={{ ...band, background: "#c0392b" }}>&#10006; ID Card Not Valid</div>
            <div style={{ padding: "30px 20px", fontSize: "clamp(14px, 4vw, 17px)" }}>This ID card could not be verified. Please contact the nearest UDS office.</div>
          </>
        )}

        {state === "error" && (
          <>
            <div style={{ ...band, background: "#c0392b" }}>Unable to verify</div>
            <div style={{ padding: "30px 20px", fontSize: "clamp(14px, 4vw, 17px)" }}>Could not reach the server. Please check your internet connection and scan again.</div>
          </>
        )}

        {state === "noToken" && (
          <>
            <div style={{ ...band, background: "#c0392b" }}>Scan the ID card again</div>
            <div style={{ padding: "30px 20px", fontSize: "clamp(14px, 4vw, 17px)" }}>For security the link is hidden after the check. Please scan the QR code on the ID card again.</div>
          </>
        )}

        <div style={{ background: ORANGE, color: "#fff", padding: 10, fontSize: 13, marginTop: 16 }}>www.uds.in</div>
      </div>
    </div>
  );
}
